import { useEffect, useMemo, useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { Ellipsis } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ConversationsDataTable } from './table/data-table';
import { conversationsColumns, type ConversationItem } from './table/columns';
import { ConversationsTableLoading } from './table/loading';
import { ViewDialog } from './view-dialog';
import { QUERY_GET_CONVERSATIONS } from '@/graphql/queries/chatKnowledge';
import { MUTATION_DELETE_CONVERSATION } from '@/graphql/mutation/chatKnowledge';
import type { ConversationListItem, ConversationsResponse } from '@/graphql/types/chatKnowledge';

export const ConversationsSection = () => {
  const apolloClient = useApolloClient();
  const { alert, showAlert, handleCloseAlert } = useAlert();

  const [loadingData, setLoadingData] = useState(false);
  const [data, setData] = useState<ConversationListItem[]>([]);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const getConversations = useCallback(async () => {
    try {
      setLoadingData(true);
      const { data } = await apolloClient.query<{ getConversations: ConversationsResponse }>({
        query: QUERY_GET_CONVERSATIONS,
        variables: {
          input: {
            limit: 100,
            offset: 0,
          },
        },
      });
      setData(data.getConversations.conversations);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient]);

  const handleDeleteConversation = useCallback(async (conversationId: string) => {
    try {
      await apolloClient.mutate({
        mutation: MUTATION_DELETE_CONVERSATION,
        variables: {
          conversation_id: conversationId,
        },
      });
      showAlert('success', 'Conversa excluída com sucesso');
      await getConversations();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, getConversations]);

  const normalizedData = useMemo(() => {
    return data.map((item) => {
      const allMessageTexts = item.messages
        ?.map((message) => message.content)
        .join(' ')
        .toLowerCase() || '';

      return {
        id: item.id,
        title: item.title,
        municipality_code: item.municipality_code,
        user_id: item.user_id,
        message_count: item.messages?.length || 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
        searchableText: allMessageTexts,
        actions: (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <Ellipsis />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent side="bottom" align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteConversation(item.id);
                }}
                className="text-red-600"
              >
                <span>Excluir Conversa</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      };
    }) as ConversationItem[];
  }, [data, handleDeleteConversation]);

  const handleRowClick = (row: ConversationItem) => {
    setSelectedConversationId(row.id);
    setOpenViewDialog(true);
  };

  useEffect(() => {
    getConversations();
  }, [getConversations]);

  return (
    <div className="flex flex-col gap-4">
      {loadingData ? (
        <ConversationsTableLoading />
      ) : (
        <ConversationsDataTable
          columns={conversationsColumns}
          data={normalizedData}
          onRowClick={handleRowClick}
        />
      )}

      <ViewDialog
        open={openViewDialog}
        onOpenChange={setOpenViewDialog}
        conversationId={selectedConversationId}
      />

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </div>
  );
};
